import * as React from 'react'
import cn from 'classnames'
import {
  defaultDropAnimation,
  defaultDropAnimationSideEffects,
  DndContext,
  DragOverlay,
} from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CardGroup } from '../../molecules/CardGroup'
import { DragHandle } from '../../atoms/DragHandle'
import { EditableTitle } from '../EditableTitle'
import { EmptyPositionCard } from '../EmptyPositionCard'
import { PositionCard } from '../PositionCard'
import { usePositionsTable } from '../../../hooks/use-positions-table'
import { useDndSensors } from '../../../hooks/use-dnd-sensors'
import { DraggablePositionCard } from './DraggablePositionCard'
import { DroppableEmptyBlockSlot } from './DroppableEmptyBlockSlot'
import { useDraggablePositions } from './use-draggable-positions'
import * as ConfirmationDialog from '../../molecules/ConfirmationDialog'
import * as OverflowMenu from '../../atoms/OverflowMenu'
import styles from './DisciplineSection.module.scss'
import type { DivPropsWithoutChildren } from '../../../types/helpers'
import type { PositionColumns } from '../../../hooks/use-positions-table/use-positions-table.types'
import type { TDiscipline } from '../../../types/entities'

export type DisciplineSectionProps = {
  id: string
  discipline?: TDiscipline
  dragHandleProps?: DivPropsWithoutChildren
  firstOfType?: boolean
} & Omit<DivPropsWithoutChildren, 'id'>

export const DisciplineSection = React.forwardRef<
  HTMLDivElement,
  DisciplineSectionProps
>((props, ref) => {
  const {
    discipline,
    className,
    dragHandleProps = {},
    firstOfType,
    ...restProps
  } = props

  const [
    permissions,
    getPositionColumns,
    getSeniorityLevels,
    eventHandlers,
    disciplines,
  ] = usePositionsTable((s) => [
    s.permissions,
    s.getPositionColumns,
    s.getSeniorityLevels,
    s.eventHandlers,
    s.disciplines,
  ])

  // disciplines triggers a recompute when hydrateProps refreshes the store.
  // getPositionColumns is a stable reference so it alone won't cause a recompute.
  const positionColumns: PositionColumns = React.useMemo(
    () => getPositionColumns(discipline?.id || 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [discipline?.id, getPositionColumns, disciplines]
  )

  const seniorityLevels = React.useMemo(() => {
    return getSeniorityLevels(discipline?.id || 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discipline?.id, getSeniorityLevels, disciplines])

  const {
    ids: draggablePositionIds,
    activePosition,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
  } = useDraggablePositions(discipline || { id: 0, name: '' })

  const sensors = useDndSensors()
  const canDrag = Boolean(permissions.allowSortPosition)

  if (!discipline) return null

  const renderColumn = (seniorityLevel: number, seniorityIndex: number) => {
    if (!positionColumns.get(seniorityLevel)) {
      if (canDrag) {
        return (
          <DroppableEmptyBlockSlot
            key={`empty-position-card-${discipline.id}-${seniorityLevel}`}
            disciplineId={discipline.id}
            discipline={discipline}
            seniorityLevel={seniorityLevel}
            allowCreatePosition={permissions.allowCreatePosition}
            onCreatePosition={eventHandlers.onCreatePosition}
            showLabels={permissions.showLabels}
          />
        )
      }
      return (
        <EmptyPositionCard
          key={`empty-position-card-${discipline.id}-${seniorityLevel}`}
          discipline={discipline}
          seniorityLevel={seniorityLevel}
          allowCreatePosition={permissions.allowCreatePosition}
          onCreatePosition={eventHandlers.onCreatePosition}
          showLabels={permissions.showLabels}
        />
      )
    }

    const PositionCardComponent = canDrag ? DraggablePositionCard : PositionCard

    return (
      <CardGroup
        orientation="vertical"
        key={`card-group-${discipline.id}-${seniorityLevel}`}
      >
        {positionColumns.get(seniorityLevel)?.map((position, positionIndex) => (
          <PositionCardComponent
            firstOfType={
              firstOfType && seniorityIndex === 0 && positionIndex === 0
            }
            position={position}
            parentDiscipline={discipline}
            permissions={permissions}
            key={`position-card-${position.id}`}
          />
        ))}
      </CardGroup>
    )
  }

  return (
    <section
      className={cn(styles.disciplineSection, className)}
      key={discipline.name}
      {...restProps}
      ref={ref}
    >
      <div className={styles.titleWrapper}>
        {permissions.allowEditDiscipline && (
          <DragHandle show className={styles.dragHandle} {...dragHandleProps} />
        )}
        <div className={styles.disciplineTitleWrapper}>
          <EditableTitle
            headingLevel={2}
            title={discipline.name}
            altTitle={`Edit discipline name: ${discipline.name}`}
            allowEdit={Boolean(permissions.allowEditDiscipline)}
            onEdit={(newTitle) =>
              eventHandlers.onEditDiscipline?.(discipline, newTitle)
            }
            titleClassName={styles.disciplineTitle}
            inputClassName={styles.disciplineTitle}
          />
        </div>
        <div className={styles.disciplinePositionsCount}>
          {discipline?.positions?.length}
        </div>
        {permissions.allowEditDiscipline && (
          <ConfirmationDialog.Root
            body={`This will also delete positions in the ${discipline.name} discipline`}
            onConfirm={() => eventHandlers.onDeleteDiscipline?.(discipline)}
            title="Are you sure?"
          >
            <OverflowMenu.Root
              className={styles.overflowMenu}
              contentClassName={styles.overflowMenuContent}
              testId={`${discipline.name
                .toLowerCase()
                .replace(/ /g, '-')}-overflow-menu`}
            >
              <OverflowMenu.Button
                onClick={() =>
                  eventHandlers.onDuplicateDiscipline?.(discipline)
                }
              >
                Duplicate
              </OverflowMenu.Button>
              <OverflowMenu.Button
                onClick={() => eventHandlers.onMoveDiscipline?.(discipline)}
              >
                Move to<span aria-hidden>..</span>
              </OverflowMenu.Button>
              <ConfirmationDialog.Trigger asChild>
                <OverflowMenu.Button variant="destructive">
                  Delete
                </OverflowMenu.Button>
              </ConfirmationDialog.Trigger>
            </OverflowMenu.Root>
          </ConfirmationDialog.Root>
        )}
      </div>

      <CardGroup className={styles.disciplineRow}>
        {canDrag ? (
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <SortableContext
              items={draggablePositionIds}
              strategy={horizontalListSortingStrategy}
            >
              {seniorityLevels.map((seniorityLevel, seniorityIndex) =>
                renderColumn(seniorityLevel, seniorityIndex)
              )}
            </SortableContext>
            <DragOverlay
              dropAnimation={{
                ...defaultDropAnimation,
                sideEffects: defaultDropAnimationSideEffects({
                  styles: { active: { opacity: '0.5' } },
                }),
              }}
            >
              {activePosition ? (
                <PositionCard
                  position={activePosition}
                  parentDiscipline={discipline}
                  permissions={permissions}
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        ) : (
          seniorityLevels.map((seniorityLevel, seniorityIndex) =>
            renderColumn(seniorityLevel, seniorityIndex)
          )
        )}
      </CardGroup>
    </section>
  )
})

DisciplineSection.displayName = 'DisciplineSection'
